import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '@/app/baseQuery';
import Jsona from 'jsona';
import decamelizeKeysDeep from 'decamelize-keys-deep';
import type { Message, MessagesResponse, ConversationsResponse } from './types/message-types';

const jsona = new Jsona();

/**
 * Helper function to serialize data for POST requests
 */
function serializeMessageData(queryParams: Record<string, any>, type: string = 'message') {
  return decamelizeKeysDeep(
    jsona.serialize({
      stuff: { ...queryParams, type },
      includeNames: queryParams.relationshipNames || []
    }),
    '-'
  );
}

interface GetConversationMessagesParams {
  ownProfileId: string;
  targetProfileId: string;
  pageNumber: number;
  pageSize: number;
}

interface SendMessageParams {
  content: string;
  ownProfileId: string;
  targetProfileId: string;
}

interface GetConversationsParams {
  profileId: string;
}

export const messagesApi = createApi({
  reducerPath: 'messagesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Messages', 'Conversations'],
  endpoints: builder => ({
    /**
     * GET /profiles/{profile-id}/messages - Get messages in a conversation
     */
    getConversationMessages: builder.query<MessagesResponse, GetConversationMessagesParams>({
      query: ({ ownProfileId, targetProfileId, pageNumber, pageSize }) => ({
        url: `profiles/${ownProfileId}/messages`,
        params: {
          'filters[other_profile_id]': targetProfileId,
          'page[number]': pageNumber,
          'page[size]': pageSize,
          sort: '-created-at',
          include: 'profile'
        }
      }),
      transformResponse: (response: any) => {
        const meta = response.meta || {};

        // If response is an array, it means it's already deserialized
        if (Array.isArray(response)) {
          return {
            data: response as Message[],
            meta: {
              totalMessages: meta.totalMessages || 0
            }
          };
        }

        // Otherwise return the response as-is
        return {
          data: response.data || [],
          meta: {
            totalMessages: meta.totalMessages || 0
          }
        };
      },
      providesTags: (_result, _error, { targetProfileId }) => [{ type: 'Messages', id: targetProfileId }]
    }),

    /**
     * POST /profiles/{profile-id}/messages - Send a new message
     */
    sendMessage: builder.mutation<Message, SendMessageParams>({
      query: ({ ownProfileId, targetProfileId, content }) => ({
        url: `profiles/${ownProfileId}/messages`,
        method: 'POST',
        body: serializeMessageData({
          content,
          receiver: { type: 'profiles', id: targetProfileId },
          relationshipNames: ['receiver']
        })
      }),
      invalidatesTags: (_result, _error, { targetProfileId }) => [
        { type: 'Messages', id: targetProfileId },
        'Conversations'
      ]
    }),

    /**
     * GET /profiles/{profile-id}/dialogs - Get all conversations for a profile
     */
    getConversations: builder.query<ConversationsResponse, GetConversationsParams>({
      query: ({ profileId }) => ({
        url: `profiles/${profileId}/dialogs`,
        params: {
          sort: '-last_message_created'
        }
      }),
      transformResponse: (response: any) => {
        const meta = response.meta || {};

        // If response is an array, it means it's already deserialized
        if (Array.isArray(response)) {
          return {
            data: response,
            meta: {
              totalDialogs: meta.totalDialogs || 0,
              unreadDialogs: meta.unreadDialogs || 0
            }
          };
        }

        // Otherwise return the response as-is
        return {
          data: response.data || [],
          meta: {
            totalDialogs: meta.totalDialogs || 0,
            unreadDialogs: meta.unreadDialogs || 0
          }
        };
      },
      providesTags: ['Conversations']
    })
  })
});

export const { useGetConversationMessagesQuery, useSendMessageMutation, useGetConversationsQuery } = messagesApi;
